/**
 * Mingle home (§15.2 #4), Offer hand and Take hand (§15.4).
 *
 * Offer hand shows the server's rotating token as a large, low-density QR code in a
 * full-brightness white frame, with the 4-character fallback code in big type. Take hand opens
 * the rear camera; "Type code instead" is always one tap away. With dev tools on, Take hand also
 * lists the other guests for a token-less `devHandshake`.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { PlayerChip } from "../components/PlayerChip.tsx";
import { QrCode } from "../components/QrCode.tsx";
import { Scanner } from "../components/Scanner.tsx";
import { cancelOffer, offerHand, send, useGame } from "../lib/store.ts";
import { useServerNow } from "../lib/time.ts";
import { living, useView } from "../lib/view.ts";
import { BallotResult } from "./Council.tsx";
import { LeakTrack } from "./RoleCard.tsx";

type Mode = "home" | "offer" | "take";

export function Mingle() {
  const view = useView();
  const phase = view.public.phase;
  const [mode, setMode] = useState<Mode>("home");
  const inHandshake = view.currentHandshake !== null;

  // A handshake started (either side): back to the home screen underneath the handshake.
  useEffect(() => {
    if (inHandshake) setMode("home");
  }, [inHandshake]);
  useEffect(() => {
    if (phase.closing) setMode("home");
  }, [phase.closing]);

  if (!view.me.alive) {
    return (
      <div className="stack" data-testid="mingle-ghost">
        <div className="hero">
          <h1>You are a Ghost.</h1>
          <p className="muted">Watch the room. You keep your voice, and your Ghost ballot.</p>
        </div>
        <LeakTrack pub={view.public} />
        <LastCouncil />
      </div>
    );
  }

  if (mode === "offer") return <OfferHand onDone={() => setMode("home")} />;
  if (mode === "take") return <TakeHand onDone={() => setMode("home")} />;

  const max = view.maxHandshakes ?? 0;
  const done = view.handshakesThisRound;
  const full = done >= max;
  return (
    <div className="stack" data-testid="mingle-home">
      <div className="hero" style={{ paddingBottom: "0.5rem" }}>
        <div className="kicker">Handshakes this round</div>
        <div className="big-number" data-testid="handshake-count">
          {done}/{max}
        </div>
        {phase.closing ? (
          <p className="muted">Time's up. Handshakes under way are finishing…</p>
        ) : full ? (
          <p className="muted">No handshakes left this round. Keep talking.</p>
        ) : (
          <p className="muted">Make 2 or 3 handshakes. Talk freely.</p>
        )}
      </div>
      <div className="toggle">
        <button
          type="button"
          className="btn primary big"
          data-testid="offer-hand"
          disabled={phase.closing || full}
          onClick={() => {
            setMode("offer");
            void offerHand();
          }}
        >
          Offer hand
        </button>
        <button
          type="button"
          className="btn primary big"
          data-testid="take-hand"
          disabled={phase.closing || full}
          onClick={() => setMode("take")}
        >
          Take hand
        </button>
      </div>
      <LeakTrack pub={view.public} />
      <LastCouncil />
    </div>
  );
}

/** The previous Council's result, shown at the start of the next round. */
function LastCouncil() {
  const view = useView();
  const round = view.public.phase.round;
  const last = view.public.revealedBallots.filter((b) => b.round === round - 1);
  if (last.length === 0) return null;
  return (
    <section className="panel">
      <h3>Council of Round {round - 1}</h3>
      {last.map((b) => (
        <BallotResult key={`${b.round}-${b.number}`} reveal={b} pub={view.public} compact />
      ))}
    </section>
  );
}

function OfferHand({ onDone }: { onDone: () => void }) {
  const offer = useGame((s) => s.offer);
  const offering = useGame((s) => s.offering);
  const now = useServerNow(1000);
  const secs = offer ? Math.max(0, Math.ceil((offer.rotatesAt - now) / 1000)) : null;

  // The offer ended without a handshake (cancelled elsewhere, phase over, refused).
  const wasOffering = useRef(false);
  useEffect(() => {
    if (offering) wasOffering.current = true;
    else if (wasOffering.current) onDone();
  }, [offering, onDone]);

  return (
    <div className="stack center" data-testid="offer-screen">
      <h2 style={{ margin: 0 }}>Let your partner scan this</h2>
      {offer ? (
        <QrCode text={offer.token} label="Your handshake QR code" />
      ) : (
        <div className="qr-frame" aria-busy="true" />
      )}
      <div>
        <div className="kicker">Or they type</div>
        <div className="fallback-code" data-testid="fallback-code">
          {offer?.code ?? "····"}
        </div>
        {secs !== null ? <div className="faint">New code in {secs} s</div> : null}
      </div>
      <button
        type="button"
        className="btn block"
        onClick={() => {
          void cancelOffer();
          onDone();
        }}
      >
        Cancel
      </button>
    </div>
  );
}

const TOKEN_RE = /^[A-Za-z0-9_-]{16,64}$/;

function TakeHand({ onDone }: { onDone: () => void }) {
  const welcome = useGame((s) => s.welcome);
  const [typing, setTyping] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const busy = useRef(false);
  const lastScan = useRef<{ text: string; at: number } | null>(null);

  const take = useCallback(async (msg: { token: string } | { code: string }) => {
    if (busy.current) return;
    busy.current = true;
    setError(null);
    const ack = await send({ type: "takeHand", ...msg }, { quiet: true });
    busy.current = false;
    if (!ack.ok) setError(ack.error?.message ?? "That did not work. Try again.");
  }, []);

  const onScan = useCallback(
    (text: string) => {
      const t = text.trim();
      const prev = lastScan.current;
      if (prev && prev.text === t && Date.now() - prev.at < 3_000) return;
      lastScan.current = { text: t, at: Date.now() };
      if (/\/join\/[A-Za-z]{4}/.test(t)) {
        setError("That is a room invitation, not a handshake code.");
        return;
      }
      if (!TOKEN_RE.test(t)) {
        setError("That is not a HANDSHAKE code.");
        return;
      }
      void take({ token: t });
    },
    [take],
  );

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (c.length !== 4) {
      setError("The code has 4 characters.");
      return;
    }
    void take({ code: c });
  };

  return (
    <div className="stack" data-testid="take-screen">
      <h2 className="center">Take a hand</h2>
      {typing ? (
        <form className="stack" onSubmit={submitCode}>
          <label className="field">
            <span>Your partner's 4-character code</span>
            <input
              className="text code"
              data-testid="code-input"
              autoFocus
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              maxLength={4}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          </label>
          <button type="submit" className="btn primary big block" data-testid="code-submit">
            Shake hands
          </button>
          <button type="button" className="btn block" onClick={() => setTyping(false)}>
            Use the camera
          </button>
        </form>
      ) : (
        <>
          <Scanner onResult={onScan} />
          <button
            type="button"
            className="btn block"
            data-testid="type-code"
            onClick={() => setTyping(true)}
          >
            Type code instead
          </button>
        </>
      )}
      {error ? (
        <p className="error-text center" role="alert" data-testid="take-error">
          {error}
        </p>
      ) : null}
      {welcome?.devTools ? <DevPartners onPick={() => setError(null)} /> : null}
      <button type="button" className="btn ghost block" onClick={onDone}>
        Back
      </button>
    </div>
  );
}

/** Dev tools only: start a handshake by tapping a partner (no token). */
function DevPartners({ onPick }: { onPick: () => void }) {
  const view = useView();
  const others = living(view.public.roster).filter((p) => p.id !== view.me.id);
  return (
    <section className="panel" data-testid="dev-partners">
      <div className="kicker">Dev tools · shake without a code</div>
      <div className="row wrap">
        {others.map((p) => (
          <button
            key={p.id}
            type="button"
            className="btn small"
            data-dev-partner={p.name}
            onClick={() => {
              onPick();
              void send({ type: "devHandshake", partnerId: p.id });
            }}
          >
            <PlayerChip player={p} />
          </button>
        ))}
      </div>
    </section>
  );
}
